import { Task, TaskStatus, TaskPriority } from '../types/task';
import { TaskCategory } from '../services/firebase/taskTemplates.service';

export interface TaskTemplateData {
  id: string;
  titleKey: string;
  descriptionKey: string;
  category: TaskCategory;
  priority: TaskPriority;
  monthsBeforeWedding: number;
  assignees: string[];
}

export const TASK_TEMPLATES_DATA: TaskTemplateData[] = [
  // 12 mois avant
  {
    id: 'choose-venue',
    titleKey: 'templates.choose-venue.title',
    descriptionKey: 'templates.choose-venue.description',
    category: 'venue',
    priority: 'high',
    monthsBeforeWedding: 12,
    assignees: []
  },
  {
    id: 'define-budget',
    titleKey: 'templates.define-budget.title',
    descriptionKey: 'templates.define-budget.description',
    category: 'other',
    priority: 'high',
    monthsBeforeWedding: 12,
    assignees: []
  },
  {
    id: 'choose-date',
    titleKey: 'templates.choose-date.title',
    descriptionKey: 'templates.choose-date.description',
    category: 'other',
    priority: 'high',
    monthsBeforeWedding: 12,
    assignees: []
  },
  {
    id: 'book-venue',
    titleKey: 'templates.book-venue.title',
    descriptionKey: 'templates.book-venue.description',
    category: 'venue',
    priority: 'high',
    monthsBeforeWedding: 10,
    assignees: []
  },
  {
    id: 'choose-photographer',
    titleKey: 'templates.choose-photographer.title',
    descriptionKey: 'templates.choose-photographer.description',
    category: 'vendors',
    priority: 'medium',
    monthsBeforeWedding: 8,
    assignees: []
  },
  {
    id: 'choose-caterer',
    titleKey: 'templates.choose-caterer.title',
    descriptionKey: 'templates.choose-caterer.description',
    category: 'vendors',
    priority: 'medium',
    monthsBeforeWedding: 8,
    assignees: []
  },
  {
    id: 'choose-florist',
    titleKey: 'templates.choose-florist.title',
    descriptionKey: 'templates.choose-florist.description',
    category: 'vendors',
    priority: 'medium',
    monthsBeforeWedding: 6,
    assignees: []
  },
  {
    id: 'choose-music',
    titleKey: 'templates.choose-music.title',
    descriptionKey: 'templates.choose-music.description',
    category: 'vendors',
    priority: 'medium',
    monthsBeforeWedding: 6,
    assignees: []
  },
  {
    id: 'choose-transport',
    titleKey: 'templates.choose-transport.title',
    descriptionKey: 'templates.choose-transport.description',
    category: 'vendors',
    priority: 'low',
    monthsBeforeWedding: 4,
    assignees: []
  },
  {
    id: 'choose-attire',
    titleKey: 'templates.choose-attire.title',
    descriptionKey: 'templates.choose-attire.description',
    category: 'attire',
    priority: 'medium',
    monthsBeforeWedding: 6,
    assignees: []
  },
  {
    id: 'choose-beauty',
    titleKey: 'templates.choose-beauty.title',
    descriptionKey: 'templates.choose-beauty.description',
    category: 'beauty',
    priority: 'low',
    monthsBeforeWedding: 3,
    assignees: []
  },
  {
    id: 'send-invitations',
    titleKey: 'templates.send-invitations.title',
    descriptionKey: 'templates.send-invitations.description',
    category: 'guests',
    priority: 'medium',
    monthsBeforeWedding: 3,
    assignees: []
  },
  {
    id: 'plan-honeymoon',
    titleKey: 'templates.plan-honeymoon.title',
    descriptionKey: 'templates.plan-honeymoon.description',
    category: 'honeymoon',
    priority: 'low',
    monthsBeforeWedding: 2,
    assignees: []
  },
  {
    id: 'legal-formalities',
    titleKey: 'templates.legal-formalities.title',
    descriptionKey: 'templates.legal-formalities.description',
    category: 'legal',
    priority: 'high',
    monthsBeforeWedding: 1,
    assignees: []
  }
];

export function createTranslatedTasks(
  templates: TaskTemplateData[],
  language: string,
  weddingDate: Date,
  weddingId: string
): Omit<Task, 'id'>[] {
  // Import dynamique des traductions selon la langue
  const getTranslation = (key: string): string => {
    const keyParts = key.split('.');
    const templateKey = keyParts[1]; // ex: 'choose-venue'
    const field = keyParts[2]; // ex: 'title' ou 'description'
    
    // Traductions hardcodées par langue
    const translations: Record<string, Record<string, Record<string, string>>> = {
      fr: {
        'choose-venue': { title: 'Choisir le lieu de réception', description: 'Rechercher et visiter différents lieux pour la cérémonie et la réception' },
        'define-budget': { title: 'Définir le budget global', description: 'Établir un budget détaillé pour chaque poste de dépense' },
        'choose-date': { title: 'Choisir la date', description: 'Fixer la date du mariage en fonction des disponibilités du lieu et des prestataires principaux' },
        'book-venue': { title: 'Réserver le lieu de réception', description: 'Visiter et réserver le lieu pour la cérémonie et la réception' },
        'choose-photographer': { title: 'Choisir le photographe', description: 'Rechercher et rencontrer des photographes pour sélectionner celui de votre mariage' },
        'choose-caterer': { title: 'Choisir le traiteur', description: 'Rechercher et sélectionner un traiteur pour la réception' },
        'choose-florist': { title: 'Choisir le fleuriste', description: 'Sélectionner un fleuriste pour les décorations de cérémonie et réception' },
        'choose-music': { title: 'Choisir la musique', description: 'Sélectionner des musiciens ou DJ pour la cérémonie et la réception' },
        'choose-transport': { title: 'Choisir le transport', description: 'Réserver le transport pour le jour du mariage' },
        'choose-attire': { title: 'Choisir les tenues', description: 'Acheter la robe de mariée et le costume du marié' },
        'choose-beauty': { title: 'Choisir les services de beauté', description: 'Réserver les services de coiffure et maquillage pour le jour du mariage' },
        'send-invitations': { title: 'Envoyer les invitations', description: 'Concevoir et envoyer les invitations de mariage aux invités' },
        'plan-honeymoon': { title: 'Planifier la lune de miel', description: 'Rechercher et réserver la destination de lune de miel' },
        'legal-formalities': { title: 'Formalités légales', description: 'Compléter toutes les exigences légales pour le mariage' }
      },
      en: {
        'choose-venue': { title: 'Choose the reception venue', description: 'Research and visit different venues for the ceremony and reception' },
        'define-budget': { title: 'Define the overall budget', description: 'Establish a detailed budget for each expense item' },
        'choose-date': { title: 'Choose the date', description: 'Set the wedding date based on venue and main vendors availability' },
        'book-venue': { title: 'Book the reception venue', description: 'Visit and book the venue for the ceremony and reception' },
        'choose-photographer': { title: 'Choose the photographer', description: 'Research and meet with photographers to select the one for your wedding' },
        'choose-caterer': { title: 'Choose the caterer', description: 'Research and select a caterer for the reception' },
        'choose-florist': { title: 'Choose the florist', description: 'Select a florist for ceremony and reception decorations' },
        'choose-music': { title: 'Choose the music', description: 'Select musicians or DJ for the ceremony and reception' },
        'choose-transport': { title: 'Choose transportation', description: 'Book transportation for the wedding day' },
        'choose-attire': { title: 'Choose wedding attire', description: 'Shop for wedding dress and groom\'s suit' },
        'choose-beauty': { title: 'Choose beauty services', description: 'Book hair and makeup services for the wedding day' },
        'send-invitations': { title: 'Send invitations', description: 'Design and send wedding invitations to guests' },
        'plan-honeymoon': { title: 'Plan honeymoon', description: 'Research and book honeymoon destination' },
        'legal-formalities': { title: 'Legal formalities', description: 'Complete all legal requirements for marriage' }
      },
      es: {
        'choose-venue': { title: 'Elegir el lugar de la recepción', description: 'Investigar y visitar diferentes lugares para la ceremonia y la recepción' },
        'define-budget': { title: 'Definir el presupuesto global', description: 'Establecer un presupuesto detallado para cada partida de gastos' },
        'choose-date': { title: 'Elegir la fecha', description: 'Fijar la fecha de la boda según la disponibilidad del lugar y los proveedores principales' },
        'book-venue': { title: 'Reservar el lugar de la recepción', description: 'Visitar y reservar el lugar para la ceremonia y la recepción' },
        'choose-photographer': { title: 'Elegir el fotógrafo', description: 'Investigar y conocer fotógrafos para seleccionar el de tu boda' },
        'choose-caterer': { title: 'Elegir el catering', description: 'Investigar y seleccionar un catering para la recepción' },
        'choose-florist': { title: 'Elegir el florista', description: 'Seleccionar un florista para las decoraciones de la ceremonia y recepción' },
        'choose-music': { title: 'Elegir la música', description: 'Seleccionar músicos o DJ para la ceremonia y la recepción' },
        'choose-transport': { title: 'Elegir el transporte', description: 'Reservar transporte para el día de la boda' },
        'choose-attire': { title: 'Elegir el vestuario', description: 'Comprar el vestido de novia y el traje del novio' },
        'choose-beauty': { title: 'Elegir servicios de belleza', description: 'Reservar servicios de peluquería y maquillaje para el día de la boda' },
        'send-invitations': { title: 'Enviar invitaciones', description: 'Diseñar y enviar invitaciones de boda a los invitados' },
        'plan-honeymoon': { title: 'Planificar la luna de miel', description: 'Investigar y reservar destino de luna de miel' },
        'legal-formalities': { title: 'Formalidades legales', description: 'Completar todos los requisitos legales para el matrimonio' }
      },
      de: {
        'choose-venue': { title: 'Empfangsort wählen', description: 'Verschiedene Orte für Zeremonie und Empfang recherchieren und besuchen' },
        'define-budget': { title: 'Gesamtbudget definieren', description: 'Detailliertes Budget für jeden Ausgabenposten erstellen' },
        'choose-date': { title: 'Datum wählen', description: 'Hochzeitsdatum basierend auf Verfügbarkeit von Ort und Hauptanbietern festlegen' },
        'book-venue': { title: 'Empfangsort buchen', description: 'Ort für Zeremonie und Empfang besuchen und buchen' },
        'choose-photographer': { title: 'Fotograf wählen', description: 'Fotografen recherchieren und treffen, um den für Ihre Hochzeit auszuwählen' },
        'choose-caterer': { title: 'Catering wählen', description: 'Catering für den Empfang recherchieren und auswählen' },
        'choose-florist': { title: 'Florist wählen', description: 'Florist für Zeremonie- und Empfangsdekorationen auswählen' },
        'choose-music': { title: 'Musik wählen', description: 'Musiker oder DJ für Zeremonie und Empfang auswählen' },
        'choose-transport': { title: 'Transport wählen', description: 'Transport für den Hochzeitstag buchen' },
        'choose-attire': { title: 'Hochzeitskleidung wählen', description: 'Hochzeitskleid und Anzug des Bräutigams einkaufen' },
        'choose-beauty': { title: 'Schönheitsdienste wählen', description: 'Frisur- und Make-up-Services für den Hochzeitstag buchen' },
        'send-invitations': { title: 'Einladungen versenden', description: 'Hochzeitseinladungen entwerfen und an Gäste versenden' },
        'plan-honeymoon': { title: 'Flitterwochen planen', description: 'Flitterwochenziel recherchieren und buchen' },
        'legal-formalities': { title: 'Rechtliche Formalitäten', description: 'Alle rechtlichen Anforderungen für die Ehe erfüllen' }
      },
      it: {
        'choose-venue': { title: 'Scegliere il luogo della reception', description: 'Ricercare e visitare diversi luoghi per la cerimonia e la reception' },
        'define-budget': { title: 'Definire il budget complessivo', description: 'Stabilire un budget dettagliato per ogni voce di spesa' },
        'choose-date': { title: 'Scegliere la data', description: 'Fissare la data del matrimonio in base alla disponibilità del luogo e dei fornitori principali' },
        'book-venue': { title: 'Prenotare il luogo della reception', description: 'Visitare e prenotare il luogo per la cerimonia e la reception' },
        'choose-photographer': { title: 'Scegliere il fotografo', description: 'Ricercare e incontrare fotografi per selezionare quello per il vostro matrimonio' },
        'choose-caterer': { title: 'Scegliere il catering', description: 'Ricercare e selezionare un catering per la reception' },
        'choose-florist': { title: 'Scegliere il fiorista', description: 'Selezionare un fiorista per le decorazioni della cerimonia e reception' },
        'choose-music': { title: 'Scegliere la musica', description: 'Selezionare musicisti o DJ per la cerimonia e la reception' },
        'choose-transport': { title: 'Scegliere il trasporto', description: 'Prenotare il trasporto per il giorno del matrimonio' },
        'choose-attire': { title: 'Scegliere l\'abbigliamento', description: 'Acquistare l\'abito da sposa e il completo dello sposo' },
        'choose-beauty': { title: 'Scegliere i servizi di bellezza', description: 'Prenotare servizi di acconciatura e trucco per il giorno del matrimonio' },
        'send-invitations': { title: 'Inviare gli inviti', description: 'Progettare e inviare inviti di matrimonio agli ospiti' },
        'plan-honeymoon': { title: 'Pianificare la luna di miele', description: 'Ricercare e prenotare destinazione per la luna di miele' },
        'legal-formalities': { title: 'Formalità legali', description: 'Completare tutti i requisiti legali per il matrimonio' }
      }
    };
    
    if (!templateKey || !field) {
      return '';
    }
    const langDict = translations[language] || translations['fr'];
    if (langDict) {
      const templateDict = langDict[templateKey] || translations['fr']?.[templateKey];
      if (templateDict) {
        return templateDict[field] || '';
      }
    }
    return '';
  };

  return templates.map(template => {
    const dueDate = new Date(weddingDate);
    dueDate.setMonth(dueDate.getMonth() - template.monthsBeforeWedding);
    
    return {
      weddingId,
      title: getTranslation(template.titleKey),
      description: getTranslation(template.descriptionKey),
      status: 'todo' as TaskStatus,
      priority: template.priority,
      category: template.category,
      assignees: template.assignees,
      dueDate,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  });
}
